import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import config from 'src/config';
import auth from './auth';
import { delay } from 'src/lib/utils/time';
import { shouldShowPublicWebsite } from 'src/lib/utils/subdomain';

const BASE_URL = config.app.apiURL.split('/v1')[0];
const MAX_RETRIES = 3;
const INITIAL_TIMEOUT = 5000;
const TIMEOUT_MULTIPLIER = 1.5;

interface RetryConfig extends InternalAxiosRequestConfig {
	retryCount?: number;
	sent?: boolean;
	abortController?: AbortController;
}

// Global abort controller for navigation-based request cancellation
let globalAbortController: AbortController | null = null;

// Function to create a new abort controller and cancel previous requests
export const createAbortController = (): AbortController => {
	// Cancel any existing requests
	if (globalAbortController) {
		globalAbortController.abort();
	}

	// Create new abort controller
	globalAbortController = new AbortController();
	return globalAbortController;
};

// Function to cancel all ongoing requests
export const cancelAllRequests = (): void => {
	if (globalAbortController) {
		globalAbortController.abort();
		globalAbortController = null;
	}
};

const calculateRetryTimeout = (retryCount: number): number => {
	return INITIAL_TIMEOUT * Math.pow(TIMEOUT_MULTIPLIER, retryCount);
};

const shouldRetryRequest = (error: any, retryCount: number): boolean => {
	const isTimeoutError = error.code === 'ECONNABORTED';
	const isNetworkError = !error.response;
	const isAbortError = error.name === 'AbortError';

	// Don't retry if the request was aborted
	if (isAbortError) return false;

	return (isTimeoutError || isNetworkError) && retryCount < MAX_RETRIES;
};

const handleAuthToken = (config: RetryConfig): void => {
	const tokens = auth.getTokens();
	if (!tokens) return;

	// Check if refresh token is expired
	const expiresAt = new Date(tokens.refresh.expires);
	const now = new Date();

	if (now.getTime() > expiresAt.getTime()) {
		auth.cleanupSession();
		// Don't redirect to login if this is a public website subdomain
		if (!shouldShowPublicWebsite()) {
			window.location.href = '/login';
		}
		return;
	}

	// Check if access token is expired (with 5 minute buffer)
	const accessExpiresAt = new Date(tokens.access.expires);
	const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds

	if (now.getTime() > accessExpiresAt.getTime() - bufferTime) {
		// Access token is expired or will expire soon, but don't redirect here
		// Let the response interceptor handle the refresh
	}

	config.headers.set('Authorization', `Bearer ${tokens.access.token}`);
	config.headers.set('Source', 'backoffice-fe');
};

const handleContentType = (config: RetryConfig): void => {
	if (config.data && !(config.data instanceof FormData)) {
		config.headers.set('Content-Type', 'application/json');
	}
};

const handleAuthError = async (
	config: RetryConfig,
	response: any,
): Promise<any> => {
	// Don't handle auth errors for login-related endpoints to prevent redirect loops
	const isLoginEndpoint =
		config.url?.includes('/auth/login') ||
		config.url?.includes('/auth/signup') ||
		config.url?.includes('/auth/forgot-password') ||
		config.url?.includes('/auth/reset-password');

	if (isLoginEndpoint) {
		return; // Let the login form handle the error
	}

	// Don't redirect to login for public website subdomains
	const isPublicWebsite = shouldShowPublicWebsite();

	// Handle both 401 and 500 errors with authentication messages
	const isAuthError =
		response.status === 401 ||
		(response.status === 500 &&
			response.data?.message?.toLowerCase().includes('authenticate'));

	if (isAuthError) {

		if (config.url?.includes('refresh-token')) {
			auth.cleanupSession();
			if (!isPublicWebsite) {
				window.location.href = '/login';
			}
			return;
		}

		if (config.url?.includes('verify-email')) {
			window.location.href = '/pending-verification';
			return;
		}

		if (!config.sent) {
			config.sent = true;
			try {
				const refreshResponse = await auth.refreshToken();
				if (refreshResponse?.tokens?.access?.token) {
					config.headers.set(
						'Authorization',
						`Bearer ${refreshResponse.tokens.access.token}`,
					);
					return axiosClient(config);
				} else {
					// If refresh token fails, redirect to login (unless public website)
					auth.cleanupSession();
					if (!isPublicWebsite) {
						window.location.href = '/login';
					}
					return;
				}
			} catch (refreshError) {
				// If refresh token fails, redirect to login (unless public website)
				auth.cleanupSession();
				if (!isPublicWebsite) {
					window.location.href = '/login';
				}
				return;
			}
		}
	}
};

const axiosClient = axios.create({
	baseURL: BASE_URL,
	withCredentials: true,
});

// URLs that should NOT be cancelled on navigation (chat, copilot, streaming endpoints)
const PROTECTED_URL_PATTERNS = [
	'/assistant',
	'/copilot',
	'/chat',
	'/thread',
	'/stream',
];

const shouldProtectFromAbort = (url: string | undefined): boolean => {
	if (!url) return false;
	return PROTECTED_URL_PATTERNS.some(pattern => url.includes(pattern));
};

const setDefaultInterceptors = (axiosInstance: AxiosInstance): void => {
	axiosInstance.interceptors.request.use((config: RetryConfig) => {
		// Add abort controller to the request if available
		// BUT skip for protected URLs (chat, copilot, etc.) so they don't get cancelled on navigation
		if (globalAbortController && !shouldProtectFromAbort(config.url)) {
			config.signal = globalAbortController.signal;
		}

		// Disable performance measurement if it's causing issues
		const enablePerformanceMeasurement = false; // Temporarily disable

		if (enablePerformanceMeasurement) {
			// Add performance mark for timing with error handling
			try {
				const requestId = `${config.method}-${config.url}-${Date.now()}`;
				performance.mark(`request-start-${requestId}`);
				(config as any).requestId = requestId;
			} catch (error) {
				console.warn('🌐 HTTP Request: Performance mark failed', {
					error,
					url: config.url,
				});
			}
		}

		handleAuthToken(config);
		handleContentType(config);
		return config;
	});

	axiosInstance.interceptors.response.use(
		(response) => {
			// Disable performance measurement if it's causing issues
			const enablePerformanceMeasurement = false; // Temporarily disable

			if (enablePerformanceMeasurement) {
				// Add performance measure with error handling
				try {
					const requestId = (response.config as any).requestId;
					if (requestId) {
						performance.mark(`request-end-${requestId}`);
						performance.measure(
							`request-${requestId}`,
							`request-start-${requestId}`,
							`request-end-${requestId}`,
						);
					}
				} catch (error) {
					console.warn('🌐 HTTP Response: Performance measure failed', {
						error,
						url: response.config.url,
					});
				}
			}

			return response;
		},
		async (error) => {
			const config = error.config as RetryConfig;

			// Don't retry if the request was aborted
			if (error.name === 'AbortError') {
				// Only log abort errors for non-external APIs and non-template searches to reduce noise
				const isExternalAPI =
					config?.url?.includes('aplo-evnt.com') ||
					config?.url?.includes('user-guiding.com');
				const isTemplateSearch = config?.url?.includes('/templates/search');

				// Silently handle aborted requests
				return Promise.reject(error);
			}

			// Check if it's a completely silenced API (no logs at all)
			const isSilencedAPI = config?.url?.includes('/v1/billing/subscriptions');

			// Check if it's an external API error that we should handle silently
			const isExternalAPI =
				config?.url?.includes('aplo-evnt.com') ||
				config?.url?.includes('user-guiding.com') ||
				config?.url?.includes('storage.googleapis.com');

			// For external APIs, only log critical errors (5xx) or network errors
			const shouldLogExternalError =
				isExternalAPI &&
				(error.response?.status >= 500 ||
					!error.response ||
					error.code === 'NETWORK_ERROR');

			// Add error logging (filtered for external APIs and silenced APIs)
			if (!isSilencedAPI && (!isExternalAPI || shouldLogExternalError)) {
				console.error('🌐 HTTP Response: Request failed', {
					method: config?.method?.toUpperCase(),
					url: config?.url,
					status: error.response?.status,
					error: error.message,
					responseData: error.response?.data,
					timestamp: new Date().toISOString(),
				});
			}

			if (!config || config.retryCount === undefined) {
				config.retryCount = 0;
			}

			// Don't retry external API errors (they have their own auth)
			if (isExternalAPI) {
				return Promise.reject(error);
			}

			// For silenced APIs (like billing), still handle auth errors but don't retry
			if (isSilencedAPI) {
				// Still try to refresh token for 401 errors on silenced APIs
				const response = error.response;
				if (response && response.status === 401) {
					const authErrorResult = await handleAuthError(config, response);
					if (authErrorResult) {
						return authErrorResult;
					}
				}
				return Promise.reject(error);
			}

			if (shouldRetryRequest(error, config.retryCount)) {
				config.retryCount++;
				const timeout = calculateRetryTimeout(config.retryCount);


				await delay(timeout);
				return axiosClient(config);
			}

			const response = error.response;
			if (response) {
				const authErrorResult = await handleAuthError(config, response);
				if (authErrorResult) {
					return authErrorResult;
				}
			}

			return Promise.reject(error);
		},
	);
};

setDefaultInterceptors(axiosClient);

export default {
	get: axiosClient.get,
	post: axiosClient.post,
	put: axiosClient.put,
	patch: axiosClient.patch,
	delete: axiosClient.delete,
	setDefaultInterceptors,
};
